import * as THREE from 'three';

const KEYS = new Set([
  'space', 'anchorId', 'position', 'target', 'projection',
  'fov', 'orthoHeight', 'near', 'far', 'width', 'height',
]);

function finiteNumber(value, name) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`${name} must be a finite number`);
  }
  return value;
}

function vector(value, name) {
  if (!Array.isArray(value) || value.length !== 3) {
    throw new Error(`${name} must be a finite three-component array`);
  }
  return value.map((component, index) => finiteNumber(component, `${name}[${index}]`));
}

function dimension(value, name) {
  if (!Number.isInteger(value) || value <= 0 || value > 4096) {
    throw new Error(`${name} must be a positive integer no greater than 4096`);
  }
  return value;
}

export function parseViewSpec(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('viewSpec must be an object');
  }
  const unknown = Object.keys(raw).find((key) => !KEYS.has(key));
  if (unknown) throw new Error(`viewSpec does not support "${unknown}"`);

  if (raw.space !== 'world' && raw.space !== 'object') {
    throw new Error('viewSpec.space must be "world" or "object"');
  }
  if (raw.space === 'object' && (typeof raw.anchorId !== 'string' || raw.anchorId.length === 0)) {
    throw new Error('viewSpec.anchorId is required when space is "object"');
  }

  const position = vector(raw.position, 'viewSpec.position');
  const target = vector(raw.target, 'viewSpec.target');
  if (position.every((value, index) => value === target[index])) {
    throw new Error('viewSpec position and target must not be the same');
  }

  const near = raw.near === undefined ? 0.01 : finiteNumber(raw.near, 'viewSpec.near');
  const far = raw.far === undefined ? 1000 : finiteNumber(raw.far, 'viewSpec.far');
  if (!(near > 0 && near < far)) throw new Error('viewSpec requires 0 < near < far');

  const common = {
    space: raw.space,
    ...(raw.space === 'object' ? { anchorId: raw.anchorId } : {}),
    position,
    target,
    projection: raw.projection,
    near,
    far,
    width: dimension(raw.width, 'viewSpec.width'),
    height: dimension(raw.height, 'viewSpec.height'),
  };

  if (raw.projection === 'perspective') {
    if (raw.orthoHeight !== undefined) {
      throw new Error('perspective viewSpec forbids orthoHeight');
    }
    if (raw.fov === undefined) throw new Error('perspective viewSpec requires fov');
    const fov = finiteNumber(raw.fov, 'viewSpec.fov');
    if (!(fov > 0 && fov < 180)) throw new Error('perspective viewSpec requires 0 < fov < 180');
    return { ...common, fov };
  }
  if (raw.projection === 'orthographic') {
    if (raw.fov !== undefined) throw new Error('orthographic viewSpec forbids fov');
    if (raw.orthoHeight === undefined) throw new Error('orthographic viewSpec requires orthoHeight');
    const orthoHeight = finiteNumber(raw.orthoHeight, 'viewSpec.orthoHeight');
    if (!(orthoHeight > 0)) throw new Error('orthographic viewSpec requires orthoHeight > 0');
    return { ...common, orthoHeight };
  }
  throw new Error('viewSpec.projection must be "perspective" or "orthographic"');
}

export function resolveViewSpec(spec, { registry }) {
  const parsed = parseViewSpec(spec);
  let position = new THREE.Vector3(...parsed.position);
  let target = new THREE.Vector3(...parsed.target);

  if (parsed.space === 'object') {
    const anchor = registry.require(parsed.anchorId);
    anchor.updateWorldMatrix(true, false);
    const worldPosition = new THREE.Vector3();
    const worldQuaternion = new THREE.Quaternion();
    const discardedScale = new THREE.Vector3();
    anchor.matrixWorld.decompose(worldPosition, worldQuaternion, discardedScale);
    const rigidWorld = new THREE.Matrix4().compose(
      worldPosition,
      worldQuaternion,
      new THREE.Vector3(1, 1, 1),
    );
    position = position.applyMatrix4(rigidWorld);
    target = target.applyMatrix4(rigidWorld);
  }

  return {
    position: position.toArray(),
    target: target.toArray(),
    projection: parsed.projection,
    ...(parsed.projection === 'perspective'
      ? { fov: parsed.fov }
      : { orthoHeight: parsed.orthoHeight }),
    near: parsed.near,
    far: parsed.far,
    width: parsed.width,
    height: parsed.height,
  };
}

/**
 * Build the camera a resolved view spec describes.
 *
 * Shared by `captureView`, which renders through it, and by `resolveSelection`'s
 * `screenRect` selector, which projects through it. Both must agree exactly on
 * what pixel a corner lands at, or a rectangle drawn against a captured image
 * would select a different set than it appears to cover — so this is one
 * function, not two that happen to match today.
 *
 * The camera is freshly constructed and never the live viewport camera.
 */
export function buildViewCamera(resolved) {
  const aspect = resolved.width / resolved.height;
  let camera;
  if (resolved.projection === 'perspective') {
    camera = new THREE.PerspectiveCamera(resolved.fov, aspect, resolved.near, resolved.far);
  } else {
    const halfHeight = resolved.orthoHeight / 2;
    const halfWidth = halfHeight * aspect;
    camera = new THREE.OrthographicCamera(
      -halfWidth, halfWidth, halfHeight, -halfHeight, resolved.near, resolved.far,
    );
  }
  camera.position.fromArray(resolved.position);
  camera.lookAt(new THREE.Vector3(...resolved.target));
  camera.updateMatrixWorld(true);
  return camera;
}
